import * as THREE from 'three';
import logger from '../lib/logger';
import { ModelAnimationController } from './animation/ModelAnimationController';
import { ModelManager } from './ModelManager';

// Forward declaration to avoid circular dependency
interface RoundResult {
  battleId: string;
  round: number;
  firstPlayer: string;
  actions: Record<string, any>;
  events: any[];
  damage: Record<string, number>;
  newHP: Record<string, number>;
  battleEnded: boolean;
  winner?: string;
}

export interface AnimationCommand {
  characterId: 'player' | 'opponent';
  action: 'attack' | 'hit' | 'block' | 'dodge' | 'death' | 'idle' | 'victory' | 'ability';
  zone?: 'head' | 'body' | 'legs';
  targetId?: 'player' | 'opponent';
  damage?: number;
  isCritical?: boolean;
  onStart?: () => void;
  onEnd?: () => void;
}

export interface AnimationEvent {
  targetId: 'player' | 'opponent';
  type: 'hit' | 'block' | 'dodge' | 'death' | 'ability';
  damage?: number;
  isCritical?: boolean;
  roundResult: RoundResult; // Pass the whole context
}

export type AnimationEventCallback = (event: AnimationEvent) => void;
export type QueueCompleteCallback = () => void;

interface AnimationPair {
  attack: AnimationCommand;
  response: AnimationCommand;
}

export class BattleAnimationManager {
  private modelControllers: Map<string, ModelAnimationController> = new Map();
  private animationQueue: Array<{ command: AnimationCommand; roundResult: RoundResult }> = [];
  private isProcessing = false;
  private modelManager: ModelManager | null = null;
  private activeSyncedAnimations: Array<{
    attackerId: string;
    targetId: 'player' | 'opponent';
    zone: 'head' | 'body' | 'legs' | null;
    animationType: 'attack' | 'ability';
    responseType: 'hit' | 'block' | 'dodge' | 'death';
    damage?: number;
    isCritical?: boolean;
    responseTriggered: boolean;
    roundResult: RoundResult;
  }> = [];

  constructor(
    private scene: THREE.Scene,
    private onAnimationEvent?: AnimationEventCallback,
    private onQueueComplete?: QueueCompleteCallback
  ) {}

  setModelManager(modelManager: ModelManager) {
    this.modelManager = modelManager;
  }

  registerModelController(characterId: string, controller: ModelAnimationController) {
    this.modelControllers.set(characterId, controller);
  }

  queueAnimations(animations: AnimationCommand[], roundResult: RoundResult) {
    logger.info('[BattleAnimationManager]', 'Queueing animations for round', roundResult.round);
    const commandsWithContext = animations.map(command => ({ command, roundResult }));
    this.animationQueue.push(...commandsWithContext);
    
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  private async processQueue() {
    if (this.animationQueue.length === 0) {
      logger.info('[BattleAnimationManager]', 'Animation queue is empty. Firing onQueueComplete.');
      this.isProcessing = false;
      this.onQueueComplete?.();
      return;
    }

    this.isProcessing = true;
    const { command, roundResult } = this.animationQueue.shift()!;
    
    // Check if this is an attack or ability with a following response
    if ((command.action === 'attack' || command.action === 'ability') && command.targetId && this.animationQueue.length > 0) {
      const nextItem = this.animationQueue[0];
      
      // Check if next command is a response to this action
      if (nextItem.command.characterId === command.targetId && 
          ['hit', 'block', 'dodge', 'death'].includes(nextItem.command.action)) {
        // Remove response from queue and process as synced pair
        this.animationQueue.shift();
        await this.processSyncedPair(command, nextItem.command, roundResult);
        // Continue processing
        this.processQueue();
        return;
      }
    }
    
    // Process single animation
    await this.processSingleAnimation(command);
    
    // Continue processing
    this.processQueue();
  }

  private async processSingleAnimation(command: AnimationCommand) {
    const controller = this.modelControllers.get(command.characterId);
    if (!controller) {
      console.error(`No controller found for character: ${command.characterId}`);
      return;
    }

    // Don't process animations for dead characters (except death itself)
    if (controller.getCurrentState() === 'death' && command.action !== 'death') {
      return;
    }

    // Execute animation
    await new Promise<void>(resolve => {
      controller.setState(command.action, command.zone, () => {
        resolve();
      }, command.onStart);
    });

    // If this was a death animation, clear any remaining animations for this character
    if (command.action === 'death') {
      this.animationQueue = this.animationQueue.filter(
        item => item.command.characterId !== command.characterId
      );
    }
  }

  private async processSyncedPair(attack: AnimationCommand, response: AnimationCommand, roundResult: RoundResult) {
    const attacker = this.modelControllers.get(attack.characterId);
    const target = this.modelControllers.get(response.characterId);
    
    if (!attacker || !target) {
      // Fallback to sequential processing
      await this.processSingleAnimation(attack);
      await this.processSingleAnimation(response);
      return;
    }

    // Determine animation type
    const isAbility = attack.action === 'ability';
    
    if (!isAbility && !attack.zone) {
      // Attack without zone - fallback
      await this.processSingleAnimation(attack);
      await this.processSingleAnimation(response);
      return;
    }

    // Get event frames based on animation type
    const eventFrames = isAbility ? 
      attacker.getAbilityEventFrames?.() : 
      (attack.zone ? attacker.getAttackEventFrames(attack.zone) : null);
    
    if (!eventFrames) {
      // No event frames, use default timing
      if (isAbility) {
        attacker.setState('ability');
      } else {
        attacker.setState('attack', attack.zone);
      }
      
      setTimeout(() => {
        target.setState(response.action as any);
      }, 600); // Default 600ms delay
      
      // Wait for animation to complete
      await new Promise<void>(resolve => {
        setTimeout(resolve, 1500);
      });
      return;
    }

    // Setup synced animation tracking
    const syncedAnimation = {
      attackerId: attack.characterId,
      targetId: response.characterId as 'player' | 'opponent',
      zone: attack.zone || null,
      animationType: isAbility ? 'ability' as const : 'attack' as const,
      responseType: response.action as 'hit' | 'block' | 'dodge' | 'death',
      damage: response.damage,
      isCritical: response.isCritical,
      responseTriggered: false,
      roundResult: roundResult
    };
    this.activeSyncedAnimations.push(syncedAnimation);

    // Start animation
    await new Promise<void>(resolve => {
      if (isAbility) {
        attacker.setState('ability', undefined, () => {
          // Remove from active animations when complete
          this.activeSyncedAnimations = this.activeSyncedAnimations.filter(
            anim => anim !== syncedAnimation
          );
          resolve();
        });
      } else {
        attacker.setState('attack', attack.zone, () => {
          // Remove from active animations when complete
          this.activeSyncedAnimations = this.activeSyncedAnimations.filter(
            anim => anim !== syncedAnimation
          );
          resolve();
        });
      }
    });
  }

  update(deltaTime: number) {
    // Update all model controllers
    this.modelControllers.forEach(controller => {
      controller.update(deltaTime);
    });

    // Check synced animation timing
    this.activeSyncedAnimations.forEach(syncedAnimation => {
      if (syncedAnimation.responseTriggered) return;
      
      const attacker = this.modelControllers.get(syncedAnimation.attackerId);
      const target = this.modelControllers.get(syncedAnimation.targetId);
      
      if (attacker && target) {
        const currentFrame = attacker.getCurrentFrame();
        
        // Get event frames based on animation type
        let eventFrames: any = null;
        if (syncedAnimation.animationType === 'ability') {
          eventFrames = attacker.getAbilityEventFrames();
        } else if (syncedAnimation.zone) {
          eventFrames = attacker.getAttackEventFrames(syncedAnimation.zone);
        }
        
        if (eventFrames) {
          let triggerFrame: number | undefined;
          
          if (syncedAnimation.animationType === 'ability') {
            // For abilities, use hit or death frame based on response type
            switch (syncedAnimation.responseType) {
              case 'hit':
                triggerFrame = eventFrames.hit;
                break;
              case 'death':
                triggerFrame = eventFrames.death;
                break;
            }
          } else {
            // For attacks, use the appropriate frame based on response type
            switch (syncedAnimation.responseType) {
              case 'hit':
                triggerFrame = eventFrames.hit;
                break;
              case 'block':
                triggerFrame = eventFrames.block;
                break;
              case 'dodge':
                triggerFrame = eventFrames.dodge;
                break;
              case 'death':
                triggerFrame = eventFrames.death;
                break;
            }
          }
          
          if (triggerFrame && currentFrame >= triggerFrame) {
            syncedAnimation.responseTriggered = true;
            target.setState(syncedAnimation.responseType);

            // Trigger the event callback
            const eventData: AnimationEvent = {
              targetId: syncedAnimation.targetId,
              type: syncedAnimation.responseType,
              damage: syncedAnimation.damage,
              isCritical: syncedAnimation.isCritical,
              roundResult: syncedAnimation.roundResult,
            };
            logger.info('[BattleAnimationManager]', 'Triggering animation event', {
              type: eventData.type,
              targetId: eventData.targetId,
              damage: eventData.damage,
              animationType: syncedAnimation.animationType
            });
            this.onAnimationEvent?.(eventData);
          }
        }
      }
    });
  }

  reset() {
    // Clear animation queue
    this.animationQueue = [];
    this.isProcessing = false;
    this.activeSyncedAnimations = [];

    // Reset all controllers to idle
    this.modelControllers.forEach(controller => {
      controller.reset();
    });
  }

  dispose() {
    this.modelControllers.forEach(controller => {
      controller.dispose();
    });
    this.modelControllers.clear();
  }
}
